import ApiService from './ApiService';

export default class extends ApiService {
    // METHODS //

    /**
     * Returns the requested cart.
     * Add to the returned cart a couponList attribute made of all the cart items coupons.
     *
     * @param {string} cartType The cart type to choose from "current", "guest", "fused"
     *
     * @returns {Promise<any>} The current cart.
     */
    async getCart(cartType) {
        let url = '/carts/' + cartType;

        const response = await this.get(url);

        // Get cart response
        let cart = response.data;

        // Foreach place
        cart.places.forEach((place) => {
            // Replace place name if online
            if (place.city == null) {
                place.name = app.locale.trans('course.online.title');
            }
        });

        // Put coupons in cart
        cart.couponList = cart.coupons;

        return cart;
    }

    /**
     * Update a cart item.
     *
     * @param {number} itemId The id of the item you want to update.
     * @param {number} price The item new price.
     * @param {number} quantity The item new quantity.
     *
     * @returns {Promise<any>} The current cart.
     */
    async updateCartItem(itemId, price, quantity) {
        let url = '/carts/current/item/' + itemId;
        let payload = {
            price: price,
            quantity: quantity,
        };

        return await this.put(url, payload);
    }

    /**
     * Add an item representing an course in the current cart.
     *
     * @param {number} ticketTypeId The course's ticket type id.
     * @param {string} ticketTypeName The course's ticket type name.
     * @param {number} price The ticket type price.
     * @param {number} quantity The quantity of item you want to add.
     * @param {number} currencyId The id of the currency used for this ticket type.
     * @param {Object} event The event choosed for this course.
     * @param {number|null} cityId The city choosed for this course. If the course is online, is null.
     *
     * @returns {Promise<any>} The data used to update GTM.
     */
    async addCartCourse(
        ticketTypeId,
        ticketTypeName,
        price,
        quantity,
        currencyId,
        event,
        cityId
    ) {
        let url = '/carts/current/course';
        let payload = {
            modelId: ticketTypeId,
            modelName: ticketTypeName,
            price: price,
            quantity: quantity,
            currencyId: currencyId,
            event: event,
            cityId: cityId,
            associatedModel: 'TicketType',
        };

        const response = await this.post(url, payload);

        // Return GTM data from response.
        return response.data.gtmData;
    }

    /**
     * Add an item representing a pack in the current cart.
     *
     * @param {number} packId The pack's id.
     * @param {string} packName The pack's name.
     * @param {number} basePrice The pack's base price.
     * @param {number} packDiscountedPrice The pack's discounted price.
     * @param {number} quantity The quantity of item you want to add.
     * @param {number} currencyId The id of the currency used for this pack.
     * @param {Array<Object>|null} events The events choosed for this pack or null if they are choosed later.
     * @param {Array<number>} ticketTypesIds The list of ticket types ids booked for this pack.
     * @param {number} cityId The city choosed for this course. If the course is online, is null.
     *
     * @returns {Promise<any>} The data used to update GTM.
     */
    async addCartPack(
        packId,
        packName,
        basePrice,
        packDiscountedPrice,
        quantity,
        currencyId,
        events,
        ticketTypesIds,
        cityId
    ) {
        let url = '/carts/current/pack';
        let payload = {
            modelId: packId,
            modelName: packName,
            basePrice: basePrice,
            packDiscountedPrice: packDiscountedPrice,
            quantity: quantity,
            currencyId: currencyId,
            events: events,
            ticketTypesIds: ticketTypesIds,
            cityId: cityId,
            associatedModel: 'Pack',
        };

        const response = await this.post(url, payload);

        // Return GTM data from response.
        return response.data.gtmData;
    }

    /**
     * Remove a cart item from the current cart.
     *
     * @param {number} itemId
     *
     * @returns {Promise<any>} The data used to update GTM.
     */
    async destroyCartItem(itemId) {
        let url = '/carts/current/item/' + itemId;

        const response = await this.delete(url);

        // Return GTM data from response.
        return response.data.gtmData;
    }
}
